import React, { useState, useContext, useEffect } from 'react'
import { Box, Drawer, Typography } from '@mui/material'
import TenantDatePicker from '../../shared/TenantDatePicker'
import Tooltip from '@mui/material/Tooltip'
import map from 'lodash/map'
import filter from 'lodash/filter'
import axios from 'axios'
import Icon from '../../shared/icon'
import { ReplenishmentOrderAction } from '../types'
import { ReplenishmentOrderItem } from './OrderItem'
import ViewMenuModal, { Menu } from './ViewMenuModal'
import { t } from '../../../i18n'
import moment from 'moment-timezone'
import { LocalizationContext, useTenantTimezone } from '../../shared/contexts'
import RemoveReplenishmentItemModal from './RemoveReplenishmentItemModal'

type ReplenishmentItemType = 'product' | 'plates' | 'oil'

type ItemForDeletion = {
  id: number | null
  type: ReplenishmentItemType | null
}

const scope = 'replenishment'

export const ReplenishmentCartSidebar = ({
  draftReplenishmentOrder, sidebarOpen, toggleSidebar,
  errorMessages, updateCart, orderState, setFlashMessage, onCartRefresh
 }) => {
  const draftReplenishmentOrderPresent = !!draftReplenishmentOrder.id

  const [itemForDeletion, setItemForDeletion] = useState<ItemForDeletion>({id: null, type: null})
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false)

  // Menu modal state
  const [menuModalOpen, setMenuModalOpen] = useState(false)
  const [menuForDate, setMenuForDate] = useState<Menu | null>(null)

  const [filterProductId, setFilterProductId] = useState<number | null>(null)
  const [filterProductName, setFilterProductName] = useState<string | null>(null)

  const { weight_scale: weightScale, date_format: dateFormat } = useContext(LocalizationContext)
  const tenantTz = useTenantTimezone()

  // Fetch menu when delivery date changes
  useEffect(() => {
    const fetchMenuForDate = async (date: Moment) => {
      try {
        const monthString = date.format('YYYY-M')
        const response = await axios.get(Routes.within_month_api_internal_menus_path(monthString))
        const menus: Menu[] = response.data
        const menu = menus.find(m => m.date === date.format('YYYY-MM-DD'))
        setMenuForDate(menu || null)
      } catch (error) {
        console.error('Error fetching menu:', error)
        setMenuForDate(null)
      }
    }

    if (draftReplenishmentOrder.deliveryDate) {
      fetchMenuForDate(moment.tz(draftReplenishmentOrder.deliveryDate, tenantTz))
    } else {
      setMenuForDate(null)
    }
  }, [draftReplenishmentOrder.deliveryDate])

  // Menu modal handlers
  const openMenuModal = () => {
    if (!menuForDate) return

    setFilterProductName(null)
    setFilterProductId(null)
    setMenuModalOpen(true)
  }

  const openMenuModalForProduct = (productId: number, productName: string) => {
    if (!menuForDate) return

    setFilterProductId(productId)
    setFilterProductName(productName)
    setMenuModalOpen(true)
  }

  const closeMenuModal = () => {
    setMenuModalOpen(false)
    setFilterProductName(null)
    setFilterProductId(null)
  }

  const hasMenuForSelectedDate = !!menuForDate

  const closeSidebar = () => {
    toggleSidebar(false)
  }

  const removePlateReplenishment = () => {
    const plateReplenishments = map(draftReplenishmentOrder.plateReplenishments, replenishment =>
      (replenishment.id === itemForDeletion.id) ? { ...replenishment, destroy: true } : replenishment
    )

    updateCart({ ...draftReplenishmentOrder, plateReplenishments }, ReplenishmentOrderAction.REMOVE_ITEM)
    setItemForDeletion({id: null, type: null})
    setShowDeleteConfirmModal(false)
  }

  const removeProductReplenishment = () => {
    const productReplenishments = map(draftReplenishmentOrder.productReplenishments, replenishment =>
      (replenishment.id === itemForDeletion.id) ? { ...replenishment, destroy: true } : replenishment
    )

    updateCart({ ...draftReplenishmentOrder, productReplenishments }, ReplenishmentOrderAction.REMOVE_ITEM)
    setItemForDeletion({id: null, type: null})
    setShowDeleteConfirmModal(false)
  }

  const removeOilReplenishment = () => {
    const oilReplenishment = {...draftReplenishmentOrder.oilReplenishment, destroy: true}
    updateCart({ ...draftReplenishmentOrder, oilReplenishment }, ReplenishmentOrderAction.REMOVE_ITEM)
    setItemForDeletion({id: null, type: null})
    setShowDeleteConfirmModal(false)
  }

  const placeOrder = () => {
    updateCart(draftReplenishmentOrder, ReplenishmentOrderAction.PLACE_ORDER)
  }

  const updateExistingOrder = () => {
    updateCart(draftReplenishmentOrder, ReplenishmentOrderAction.UPDATE_SENT_ORDER)
  }

  const openDeleteConfirmModal = (replenishmentItemId: number, type: ReplenishmentItemType) => {
    setItemForDeletion({id: replenishmentItemId, type: type})
    setShowDeleteConfirmModal(true)
  }

  const productReplenishmentsOfType = (type: 'solid' | 'liquid') => {
    const productReplenishments = filter(draftReplenishmentOrder.productReplenishments, replenishment =>
      replenishment.containerType === type && !replenishment.destroy
    )
    const header = type.charAt(0).toUpperCase() + type.slice(1)

    if(!productReplenishments.length)
      return
    return (
      <div className={`${type}-ingredients`} style={{marginTop: '20px'}}>
        <span className='sidebar-heading replenishment-order-subcategory'>{header} ingredients</span>
        {productReplenishments.map((replenishment, index) =>
          <ReplenishmentOrderItem
            key={`${replenishment.product.id}-${index}`}
            type="product"
            orderItem={replenishment}
            index={index}
            removeReplenishment={openDeleteConfirmModal}
            weightScale={weightScale}
            onViewDishes={openMenuModalForProduct}
          />
        )}
      </div>
    )
  }

  const plateReplenishments = () => {
    if(!draftReplenishmentOrder.plateReplenishments.length)
      return
    const plateReplenishments = draftReplenishmentOrder.plateReplenishments.filter(replenishment => !replenishment.destroy)
    return (
      <div className="plates" style={{marginTop: '20px'}}>
        <span className='sidebar-heading replenishment-order-subcategory'>{t('sidebar.bowls', { scope })}</span>
        {plateReplenishments.map((replenishment, index) =>
          <ReplenishmentOrderItem
            key={`${replenishment.slotNumber}-${index}`}
            type="plates"
            orderItem={replenishment}
            index={index}
            removeReplenishment={openDeleteConfirmModal}
            weightScale={weightScale}
          />
        )}
      </div>
    )
  }

  const oilReplenishment = () => {
    const replenishment = draftReplenishmentOrder.oilReplenishment
    if(!replenishment || replenishment.destroy)
      return
    return (
      <div className="plates" style={{marginTop: '20px'}}>
        <span className='sidebar-heading replenishment-order-subcategory'>Oil tank</span>
        <ReplenishmentOrderItem
          type="oil"
          index={0}
          orderItem={replenishment}
          removeReplenishment={openDeleteConfirmModal}
          weightScale={weightScale}
        />
      </div>
    )
  }

  const cartEmpty = () => {
    const oilReplenishmentEmpty = (obj) => {
      if(!obj) return true
      return Object.values(obj).every(val => !val);
    }
    return draftReplenishmentOrder.plateReplenishments.length == 0 && draftReplenishmentOrder.productReplenishments.length == 0 && oilReplenishmentEmpty(draftReplenishmentOrder.oilReplenishment)
  }

  const cancel = () => {
    if (!confirm(t('global.confirm'))) return

    updateCart(draftReplenishmentOrder, ReplenishmentOrderAction.CANCEL)
    closeSidebar()
  }

  const onDrawerClose = (e: React.SyntheticEvent, reason: string) => {
    if ((reason === 'escapeKeyDown' || reason === 'backdropClick') && menuModalOpen) {
      e.preventDefault()
      closeMenuModal()
    } else if ((reason === 'escapeKeyDown' || reason === 'backdropClick') && showDeleteConfirmModal) {
      e.preventDefault()
      setShowDeleteConfirmModal(false)
    } else {
      closeSidebar()
    }
  }

  const changeHistoryUrl = `/replenishment_orders/${draftReplenishmentOrder.id}/product_replenishments_history`

  return (
    <div>
      {showDeleteConfirmModal &&
        <RemoveReplenishmentItemModal
          itemForDeletion={itemForDeletion}
          removePlateReplenishment={removePlateReplenishment}
          removeProductReplenishment={removeProductReplenishment}
          removeOilReplenishment={removeOilReplenishment}
          setShowDeleteConfirmModal={setShowDeleteConfirmModal}
        />
      }
      {menuModalOpen && draftReplenishmentOrder.deliveryDate &&
        <ViewMenuModal
          date={moment.tz(draftReplenishmentOrder.deliveryDate, tenantTz)}
          menuForDate={menuForDate}
          setMenuForDate={setMenuForDate}
          onClose={closeMenuModal}
          filterProductId={filterProductId}
          title={filterProductName ? `${t('sidebar.menu_modal.dishes_with', { scope })} ${filterProductName}` : undefined}
          setFlashMessage={setFlashMessage}
          onCartRefresh={onCartRefresh}
        />
      }
      <Drawer
        className='replenishment-sidebar'
        anchor='right'
        open={sidebarOpen}
        onClose={onDrawerClose}
        style={{zIndex: '2 !important'}}
      >
        <Box
          sx={{ width: 400, padding: '20px 15px', borderRadius: '10px 0 0 10px', zIndex: '2 !important' }}
          role="presentation"
        >
          <div className="replenishment-sidebar-content">
            <div className="sidebar-top">
              <div className='sidebar-header'>
                {orderState === 'editing' &&
                  <>
                    <span className='sidebar-heading'>Order #{draftReplenishmentOrder.editableOrderId}</span>
                    <span className='sidebar-editing'>Editing</span>
                  </>
                }
                {(orderState !== 'editing' && !draftReplenishmentOrderPresent) && <span className='sidebar-heading'>{t('sidebar.heading', { scope })}</span>}
                {(orderState !== 'editing' && draftReplenishmentOrderPresent) && <Tooltip title="Click to view order change history" placement='bottom' arrow>
                  <span className='sidebar-heading'>
                    <a href={changeHistoryUrl} target="_blank" rel="noopener noreferrer">
                      {t('sidebar.heading', { scope })}
                      <div style={{ display: 'inline-block', marginLeft: '5px' }}><Icon name='external' /></div>
                    </a>
                  </span>
                </Tooltip>
                }
                <Icon name='close' className="close-icon" onClick={closeSidebar}/>
              </div>
              {orderState == 'sent' && <><br/><span>{t('sidebar.order_sent_editing_message', { scope })}</span></>}
              <div className="replenishment-errors">
                {Object.keys(errorMessages).map(message => {return (
                  <span key={message}>
                    {errorMessages[message]}
                  </span>
                )})}
              </div>
              {orderState !== 'sent' &&
                <div className={`replenishment-date ${errorMessages.hasOwnProperty('delivery_date') ? 'has-error' : ''}`}>
                  <span>{t('sidebar.replenish_date', { scope })}</span>
                  <TenantDatePicker className="replenish-datepicker"
                    format={dateFormat}
                    disablePast={true}
                    value={draftReplenishmentOrder.deliveryDate ? moment.tz(draftReplenishmentOrder.deliveryDate, tenantTz) : null}
                    onChange={date => updateCart({...draftReplenishmentOrder, deliveryDate: date}, ReplenishmentOrderAction.CHANGE_DATE)}
                  />
                </div>
              }
              <button
                className={`btn btn-primary mt-3 ${!hasMenuForSelectedDate ? 'disabled' : ''}`}
                disabled={!hasMenuForSelectedDate}
                onClick={openMenuModal}
                style={{width: '100%'}}
              >
                {t('sidebar.view_full_menu', { scope }) + (draftReplenishmentOrder.deliveryDate ? moment.tz(draftReplenishmentOrder.deliveryDate, tenantTz).format(' DD/MM') : ' selected date')}
              </button>
            </div>
            {orderState != 'sent' &&
              <>
                {cartEmpty() &&
                <div className='empty-cart-container'>
                  <span>{t('sidebar.basket_empty', { scope })}</span>
                  <Icon name='replenishmentEmptyCart'/>
                </div>
                }
                <div>
                  {productReplenishmentsOfType('solid')}
                  {productReplenishmentsOfType('liquid')}
                  {plateReplenishments()}
                  {oilReplenishment()}
                </div>
                <div className="replenishment-buttons">
                  {orderState == 'draft' &&
                    <button
                      className={`btn btn-primary mt-3 ${cartEmpty() ? 'disabled' : ''}`}
                      disabled={cartEmpty()}
                      onClick={() => placeOrder()}>
                        {t('sidebar.place_order_btn', { scope })}
                    </button>
                  }
                  {orderState == 'editing' &&
                    <button
                      className={`btn btn-primary mt-3 ${cartEmpty() ? 'disabled' : ''}`}
                      disabled={cartEmpty()}
                      onClick={() => updateExistingOrder()}>
                        {t('sidebar.update_order_btn', { scope })}
                    </button>
                  }
                  <button
                    className={`btn btn-danger ${!draftReplenishmentOrderPresent ? 'disabled' : ''}`}
                    onClick={cancel}
                    disabled={!draftReplenishmentOrderPresent}
                  >
                    {t('sidebar.cancel_order_btn', { scope })}
                  </button>
                </div>
              </>
            }
          </div>
        </Box>
      </Drawer>
    </div>
  )
}
